import React from 'react';
import { ClipboardCheck, PenLine, HardHat, Award, Clock, AlertTriangle } from 'lucide-react';

// =====================================================================
// Certificação — PLACAR da tela de relatórios (2026-09-03).
//
// POR QUÊ: a tela listava 25 linhas visualmente iguais e não respondia as
// perguntas de quem toca o programa — quantos estão em aberto, quantos esperam
// assinatura (e de quem), quantos estão na Etapa 1 e quantos na Etapa 2, e como
// vai cada equipamento. Medido em 03/09/2026, o fato que domina o programa é
// que **21 dos 23 relatórios abertos esperam a assinatura de um gerente**, e
// isso não aparecia em lugar nenhum da tela.
//
// ⚠️ TODO BLOCO FILTRA A TABELA — o placar não é decoração: clicar em
// "Aguardando validação" mostra os 21. Um número que não leva ao trabalho
// obriga a pessoa a reconstruir o filtro na mão e é onde ela desiste.
//
// ⚠️ AS CONTAGENS VÊM PRONTAS de `certificacao-placar-utils.js` (puro, testado
// contra a foto real do banco). Aqui é só desenho: nenhuma conta nasce neste
// arquivo — senão a mesma regra passaria a existir em dois lugares.
// =====================================================================

const COR = '#4f46e5';

// Paleta dos blocos. A cor diz DE QUEM é a bola: âmbar = falta assinatura
// (alguém de fora da Manutenção), índigo = está com a Manutenção, verde =
// terminou. É a mesma convenção da coluna "Aguardando" da tabela.
const TOM = {
  aberto: { cor: '#334155', fundo: '#f1f5f9', borda: '#cbd5e1' },
  validacao: { cor: '#92400e', fundo: '#fef3c7', borda: '#fcd34d' },
  trabalho: { cor: '#4338ca', fundo: '#eef2ff', borda: '#c7d2fe' },
  certificado: { cor: '#065f46', fundo: '#d1fae5', borda: '#6ee7b7' },
};

const num = { fontVariantNumeric: 'tabular-nums' };

// Um bloco do placar: rótulo, número grande, e a linha que diz o que o número
// significa. Clicável = aplica o filtro correspondente na tabela.
function Bloco({ tom, icone: Icone, rotulo, valor, detalhe, ativo, onClick, compacto }) {
  const t = TOM[tom];
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Filtrar a lista: ${rotulo.toLowerCase()}`}
      style={{
        flex: compacto ? '1 1 45%' : '1 1 0',
        minWidth: compacto ? 0 : 170,
        textAlign: 'left',
        background: ativo ? t.fundo : '#fff',
        border: `${ativo ? 2 : 1}px solid ${ativo ? t.borda : '#e2e8f0'}`,
        borderRadius: 12,
        padding: ativo ? '13px 15px' : '14px 16px', // compensa a borda: não pula ao selecionar
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 6,
        transition: 'background .15s, border-color .15s',
      }}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 800, letterSpacing: .3,
        textTransform: 'uppercase', color: t.cor,
      }}>
        <Icone size={14} /> {rotulo}
      </span>
      <span style={{ ...num, fontSize: 30, fontWeight: 800, color: t.cor, lineHeight: 1 }}>{valor}</span>
      {/* O detalhe é o que impede o número de ser lido errado ("21 de quê?"). */}
      <span style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4, minHeight: 32 }}>{detalhe}</span>
    </button>
  );
}

// Barra de cobertura de um equipamento. ⚠️ Limitada a 100%: `feito` pode passar
// de `total` quando um equipamento certificado foi baixado da loja (sai do
// parque e não do histórico). A barra satura, os números crus ao lado contam a
// verdade — ver a nota em resumirPorEquipamento.
function Barra({ feito, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((feito / total) * 100)) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: COR, borderRadius: 999 }} />
      </div>
      <span style={{ ...num, fontSize: 12, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
        {total > 0 ? `${feito} de ${total}` : `${feito} sem parque cadastrado`}
      </span>
    </div>
  );
}

/**
 * @param resumo        saída de resumirFila()
 * @param porEquipamento saída de resumirPorEquipamento() (só os que estão no programa)
 * @param foraDoPrograma nomes dos tipos do catálogo ainda sem formulário publicado
 * @param selecao       { aba, etapa, aguardando, equipamento } — o que está filtrado agora
 * @param onFiltrar     recebe o patch de filtro do bloco clicado
 * @param escopo        texto do recorte atual (loja/período), para o placar não mentir
 */
export default function PlacarCertificacao({
  resumo, porEquipamento = [], foraDoPrograma = [],
  selecao = {}, onFiltrar, escopo = '', periodoAtivo = false, compacto = false,
}) {
  const { aba, etapa, aguardando, equipamento } = selecao;

  // Nenhum relatório no recorte: o placar cheio de zeros só ocupa espaço.
  if (!resumo || resumo.total === 0) return null;

  const rotuloEtapa = (n) => (n === 1 ? 'Etapa 1 · Diagnóstico' : 'Etapa 2 · Certificação');
  const etapaTotal = resumo.etapa1 + resumo.etapa2;

  const cardEtapa = (n, valor) => {
    // ⚠️ Comparação em TEXTO: este bloco manda a etapa como número e o select da
    // barra de filtros manda string — com `===` cru, escolher pelo select
    // deixaria o card correspondente apagado.
    const ativo = String(etapa || '') === String(n);
    return (
      <button
        type="button"
        onClick={() => onFiltrar({ aba: 'andamento', etapa: ativo ? '' : n, aguardando: '', status: '' })}
        title={`Filtrar a lista: ${rotuloEtapa(n)}`}
        style={{
          flex: 1, minWidth: compacto ? 0 : 200, textAlign: 'left', cursor: 'pointer',
          background: ativo ? '#eef2ff' : '#fff',
          border: `${ativo ? 2 : 1}px solid ${ativo ? '#c7d2fe' : '#e2e8f0'}`,
          borderRadius: 12, padding: ativo ? '11px 13px' : '12px 14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: .3 }}>
            {rotuloEtapa(n)}
          </span>
          <span style={{ ...num, marginLeft: 'auto', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{valor}</span>
        </div>
        {/* A barra compara as duas etapas entre si: é a leitura de "o programa
            está travado no diagnóstico?" — hoje, 21 contra 2. */}
        <div style={{ marginTop: 8, height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{
            width: etapaTotal ? `${Math.round((valor / etapaTotal) * 100)}%` : '0%',
            height: '100%', background: COR, borderRadius: 999,
          }} />
        </div>
      </button>
    );
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* ---------------- Fila: de quem é a bola ---------------- */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Bloco
          tom="aberto" icone={ClipboardCheck} rotulo="Em aberto" valor={resumo.emAberto}
          compacto={compacto}
          ativo={aba === 'andamento' && !etapa && !aguardando}
          onClick={() => onFiltrar({ aba: 'andamento', etapa: '', aguardando: '', status: '' })}
          detalhe={
            resumo.parados14 > 0
              ? <>de {resumo.total} relatórios · <strong style={{ color: resumo.parados30 ? '#b91c1c' : '#b45309' }}>
                {resumo.parados14} parados há mais de 14 dias</strong>{resumo.piorParado !== null ? ` (pior: ${resumo.piorParado})` : ''}</>
              : <>de {resumo.total} relatórios no total</>
          }
        />
        <Bloco
          tom="validacao" icone={PenLine} rotulo="Aguardando validação" valor={resumo.aguardandoValidacao}
          compacto={compacto}
          ativo={aguardando === 'validacao'}
          onClick={() => onFiltrar({ aba: 'andamento', aguardando: aguardando === 'validacao' ? '' : 'validacao', etapa: '', status: '' })}
          // ⚠️ "em 21 · em 2" e NÃO "21 + 2": quando os dois papéis devem
          // assinatura, o relatório conta nos dois — somar dá número inexistente.
          detalhe={<>falta o <strong>gerente em {resumo.faltaGerente}</strong> · o consultor em {resumo.faltaConsultor}</>}
        />
        <Bloco
          tom="trabalho" icone={HardHat} rotulo="Com a Manutenção" valor={resumo.comManutencao}
          compacto={compacto}
          ativo={aguardando === 'trabalho'}
          onClick={() => onFiltrar({ aba: 'andamento', aguardando: aguardando === 'trabalho' ? '' : 'trabalho', etapa: '', status: '' })}
          detalhe="preenchimento, iniciar ou concluir a Etapa 2 — não depende de assinatura"
        />
        <Bloco
          tom="certificado" icone={Award} rotulo="Certificados" valor={resumo.certificados}
          compacto={compacto}
          ativo={aba === 'certificados'}
          onClick={() => onFiltrar({ aba: 'certificados', etapa: '', aguardando: '', status: '' })}
          detalhe={resumo.cancelados ? `${resumo.cancelados} cancelado(s) fora da conta` : 'validados por gerente e consultor'}
        />
      </div>

      {/* O placar mede o RECORTE, não a rede: sem dizer isso, "21 aguardando"
          com a loja ANV escolhida se lê como o número da rede inteira. */}
      {escopo && (
        <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
          Números do recorte atual: {escopo}.
        </p>
      )}

      {/* ---------------- Em que etapa o trabalho está ---------------- */}
      {etapaTotal > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
          {cardEtapa(1, resumo.etapa1)}
          {cardEtapa(2, resumo.etapa2)}
        </div>
      )}

      {/* ---------------- Como vai cada equipamento ---------------- */}
      {porEquipamento.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginTop: 12, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <strong style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: .3 }}>
              Equipamentos no programa
            </strong>
            {/* ⚠️ A cobertura é ACUMULADA (todo o histórico), então ela ignora o
                filtro de período de propósito — e precisa dizer isso, senão os
                números do painel discordam da tabela sem explicação. */}
            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>
              cobertura acumulada do parque{periodoAtivo ? ' — não segue o filtro de período' : ''}
            </span>
          </div>

          {porEquipamento.map((eq, i) => {
            const ativo = equipamento === eq.nome;
            return (
              <div
                key={eq.nome}
                onClick={() => onFiltrar({ equipamento: ativo ? '' : eq.nome })}
                title={`Filtrar a lista: ${eq.nome}`}
                style={{
                  padding: '12px 16px', cursor: 'pointer',
                  borderTop: i ? '1px solid #f1f5f9' : 'none',
                  background: ativo ? '#eef2ff' : '#fff',
                  borderLeft: `3px solid ${ativo ? COR : 'transparent'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <strong style={{ fontSize: 14.5, color: '#0f172a' }}>{eq.nome}</strong>
                  <span style={{ ...num, fontSize: 13, color: '#64748b' }}>
                    {eq.total} {eq.total === 1 ? 'relatório' : 'relatórios'}
                  </span>
                  <span style={{ ...num, marginLeft: 'auto', fontSize: 12.5, color: '#475569' }}>
                    {eq.etapa1 > 0 && <>{eq.etapa1} em Etapa 1</>}
                    {eq.etapa1 > 0 && (eq.etapa2 > 0 || eq.certificados > 0) && ' · '}
                    {eq.etapa2 > 0 && <>{eq.etapa2} em Etapa 2</>}
                    {eq.etapa2 > 0 && eq.certificados > 0 && ' · '}
                    {/* Zero certificados é informação, não ausência: é o que diz
                        que o equipamento entrou e ainda não fechou nenhum ciclo. */}
                    <span style={{ color: eq.certificados ? '#047857' : '#94a3b8', fontWeight: eq.certificados ? 700 : 400 }}>
                      {eq.certificados} certificado{eq.certificados === 1 ? '' : 's'}
                    </span>
                  </span>
                </div>
                <Barra feito={eq.ativosComRelatorio} total={eq.parque} />
                {/* Equipamento sem formulário publicado pode ser agendado e não
                    preenchido — quem vê "0 relatórios" precisa saber por quê. */}
                {eq.versoesPublicadas === 0 && (
                  <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <AlertTriangle size={13} /> sem formulário publicado — não é possível preencher
                  </p>
                )}
              </div>
            );
          })}

          {foraDoPrograma.length > 0 && (
            <div style={{
              padding: '10px 16px', borderTop: '1px solid #f1f5f9', background: '#f8fafc',
              fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Clock size={13} />
              {foraDoPrograma.length} {foraDoPrograma.length === 1 ? 'tipo' : 'tipos'} no catálogo ainda sem
              formulário publicado: {foraDoPrograma.join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
